import { useMutation, useQuery } from '@tanstack/react-query';
import {
  listIntegrations,
  testFreeIntegrations,
  testIntegration,
  type IntegrationTestResult,
  type IntegrationTestSweep,
} from '@/lib/integrations-api';

/**
 * The integrations inventory and its connection tests (spec § 4.4).
 *
 * ── RESULTS ARE NOT CACHED, AND THAT IS THE POINT ─────────────────────────────────
 *
 * A test result is a statement about one moment. Caching it would mean a screen saying "SendGrid:
 * valid" long after the key was rotated, which is worse than saying nothing — somebody would trust
 * it. So the results live in the mutation and in the page's own state, never in the query cache, and
 * they carry no age beyond "you just pressed this".
 *
 * The INVENTORY is cached, because it comes from configuration that cannot change without a restart.
 */

export const integrationKeys = {
  list: () => ['integrations'] as const,
};

export function useIntegrations() {
  return useQuery({
    queryKey: integrationKeys.list(),
    queryFn: listIntegrations,
    // Configuration, read once at startup on the server. Re-asking on every visit would be a request
    // that always returns the same answer.
    staleTime: Infinity,
  });
}

/**
 * Tests one dependency.
 *
 * Deliberately does not invalidate the inventory: a test tells you whether a credential WORKS and
 * changes nothing about whether one is present, so refetching the list afterwards would be a request
 * that cannot return anything new.
 */
export function useTestIntegration(
  onResult: (key: string, result: IntegrationTestResult) => void,
) {
  return useMutation({
    mutationFn: (key: string) => testIntegration(key),
    onSuccess: (result, key) => onResult(key, result),
  });
}

export function useTestFreeIntegrations(onSweep: (sweep: IntegrationTestSweep) => void) {
  return useMutation({
    mutationFn: testFreeIntegrations,
    onSuccess: (sweep) => onSweep(sweep),
  });
}
